import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { squeezoService } from '../services/squeezoService';
import { usePermissions } from '../hooks/usePermissions';
import { getSqueezoZoneColor } from '../utils/squeezoColors';
import { fmtUsDate } from './TickerDetail/format';
import { TZ } from '../utils/marketTime';

interface SqueezoChartModalProps {
  currentValue: number;
  onClose: () => void;
}

// Chart dimensions
const CHART_W = 560;
const CHART_H = 220;
const PAD = { top: 15, right: 15, bottom: 30, left: 35 };
const PLOT_W = CHART_W - PAD.left - PAD.right;
const PLOT_H = CHART_H - PAD.top - PAD.bottom;

export const SqueezoChartModal: React.FC<SqueezoChartModalProps> = ({ currentValue, onClose }) => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('jdash_access');
  const queryClient = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);

  // Edit controls state
  const [inputValue, setInputValue] = useState<number>(currentValue);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  // Explainer is opt-in. It is the kind of thing you read once and then never
  // want occupying the top of the panel again.
  const [showHelp, setShowHelp] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['squeezo', 'history', 200],
    queryFn: () => squeezoService.getHistory(200),
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (value: number) => squeezoService.createEntry(value),
    onSuccess: () => {
      setStatus('success');
      queryClient.invalidateQueries({ queryKey: ['squeezo'] });
      setTimeout(() => setStatus('idle'), 1500);
    },
    onError: () => {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    },
  });

  const handleSubmit = (): void => {
    if (inputValue < 0 || inputValue > 100) return;
    if (inputValue === currentValue) return;
    createMutation.mutate(inputValue);
  };

  const incrementBy = (amount: number): void => {
    setInputValue((v) => Math.min(100, Math.max(0, v + amount)));
  };

  const hasChanged = inputValue !== currentValue;

  // Reverse for left=oldest, right=newest
  const points = data?.data ? [...data.data].reverse() : [];

  // Newest reading's stamp. Taken from the data rather than "now" — the value
  // is set by hand and can be hours old, so a fetch time would overstate it.
  const lastReadingAt = points.length ? points[points.length - 1].created_at : null;

  /**
   * The reading history as CSV.
   *
   * Built from the rows already in the query cache, so it costs no request and
   * exports exactly what the chart is drawing.
   */
  const handleExport = (): void => {
    if (!points.length) return;
    setIsExporting(true);
    try {
      const rows = [
        'timestamp_et,value,zone',
        ...points.map((p) => {
          const stamp = new Date(p.created_at).toLocaleString('sv-SE', { timeZone: TZ });
          const zone = p.value >= 65 ? 'retail extreme bullish'
            : p.value <= 45 ? 'retail extreme bearish'
            : 'neutral';
          return `${stamp},${p.value},${zone}`;
        }),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([rows], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `squeezo-history-${new Date().toLocaleDateString('en-CA', { timeZone: TZ })}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  // Dynamic Y-axis: fit to data range but always include the 45 and 65 thresholds
  const values = points.map((p) => p.value);
  const dataMin = values.length > 0 ? Math.min(...values) : 0;
  const dataMax = values.length > 0 ? Math.max(...values) : 100;
  const yLow = Math.max(0, Math.floor(Math.min(dataMin, 45) / 5) * 5 - 5);
  const yHigh = Math.min(100, Math.ceil(Math.max(dataMax, 65) / 5) * 5 + 5);
  const yRange = yHigh - yLow || 1;

  // Convert a squeezo value to SVG y coordinate
  const toY = (v: number): number => PAD.top + PLOT_H - ((v - yLow) / yRange) * PLOT_H;
  // Convert a point index to SVG x coordinate
  const toX = (i: number): number =>
    points.length > 1 ? PAD.left + (i / (points.length - 1)) * PLOT_W : PAD.left;

  // Which threshold lines to show (only if within visible range)
  // 45 and 65 only. The 50 line marked no threshold — nothing changes there,
  // no zone starts or ends — so it read as a boundary that does not exist. The
  // two that remain are the actual zone edges.
  const thresholds = [45, 65].filter((v) => v >= yLow && v <= yHigh);

  // Mouse hover handler
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || points.length < 2) return;
      const rect = svgRef.current.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * CHART_W;
      const plotX = mouseX - PAD.left;
      if (plotX < 0 || plotX > PLOT_W) {
        setHoveredIdx(null);
        return;
      }
      const idx = Math.round((plotX / PLOT_W) * (points.length - 1));
      setHoveredIdx(Math.max(0, Math.min(points.length - 1, idx)));
    },
    [points.length],
  );

  const handleMouseLeave = useCallback(() => setHoveredIdx(null), []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-lg shadow-xl max-w-2xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header. Title treatment matches the Squeezo widget's own — Archivo
            bold, uppercase, 1.5px tracking — rather than the sentence-case
            heading this modal had, which was the odd one out among the tools.

            `relative`, with the title group absolutely centred: a flex
            `justify-center` would centre it in the space LEFT OVER by the close
            button, which is off by half that button's width. */}
        <div className="relative flex items-center justify-end px-4 py-3 border-b border-dark-border">
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
            <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
              Squeezo History
            </h2>
            <button
              onClick={() => setShowHelp((v) => !v)}
              aria-expanded={showHelp}
              aria-label="What does this measure?"
              title="What does this measure?"
              className={`w-4 h-4 flex items-center justify-center rounded-full border text-[10px] font-bold leading-none transition-colors ${
                showHelp
                  ? 'border-accent-yellow text-accent-yellow'
                  : 'border-dark-border text-gray-400 hover:text-white hover:border-gray-500'
              }`}
            >
              ?
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || !points.length}
              title="Download the reading history as CSV"
              className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-dark-border text-[10px] uppercase tracking-wider text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {isExporting ? 'Exporting…' : 'CSV'}
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white hover:bg-bear-red/20 px-2 py-0.5 rounded transition-colors text-sm"
          >
            ×
          </button>
        </div>

        {/* Explainer, behind the ? in the header.
            The index measures RETAIL sentiment but is read as a CONTRARIAN
            signal — those are opposite, so a reader who does not know that will
            take every number backwards. Worth having, not worth sitting above
            the chart on every open. */}
        {showHelp && (
          <div className="px-4 pt-4">
            <div className="rounded border border-dark-border bg-dark-bg px-3 py-2">
              <p className="text-[11px] text-gray-300 leading-relaxed">
                Contrarian read of retail sentiment: high means retail is stretched
                bullish, low means stretched bearish. At an extreme, positioning is
                one-sided &mdash; worth extra caution on that side, or you can look to
                fade the current sentiment. No timeframe implied.
              </p>
            </div>
          </div>
        )}

        {/* Chart */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">
              Loading history...
            </div>
          ) : points.length < 2 ? (
            <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">
              Not enough data for chart
            </div>
          ) : (
            <svg
              ref={svgRef}
              width="100%"
              viewBox={`0 0 ${CHART_W} ${CHART_H}`}
              className="overflow-visible"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {/* Zone backgrounds (clipped to visible Y range) */}
              {/* Red zone: 65 to yHigh */}
              {yHigh > 65 && (
                <rect
                  x={PAD.left}
                  y={toY(yHigh)}
                  width={PLOT_W}
                  height={toY(65) - toY(yHigh)}
                  fill="rgba(216, 72, 59, 0.06)"
                />
              )}
              {/* Bullish zone (bull-blue): yLow to 45 */}
              {yLow < 45 && (
                <rect
                  x={PAD.left}
                  y={toY(45)}
                  width={PLOT_W}
                  height={toY(yLow) - toY(45)}
                  fill="rgba(28, 118, 162, 0.06)"
                />
              )}

              {/* Y-axis gridlines: bounds + thresholds */}
              {/* Bottom/top bounds */}
              {[yLow, yHigh].map((v) => (
                <g key={`bound-${v}`}>
                  <line
                    x1={PAD.left}
                    y1={toY(v)}
                    x2={PAD.left + PLOT_W}
                    y2={toY(v)}
                    stroke="#2B2B2B"
                    strokeWidth="1"
                  />
                  <text
                    x={PAD.left - 6}
                    y={toY(v)}
                    textAnchor="end"
                    dominantBaseline="middle"
                    className="fill-gray-500 text-[10px]"
                  >
                    {v}
                  </text>
                </g>
              ))}
              {/* Threshold lines */}
              {thresholds.map((v) => {
                const lineColor = v === 65 ? '#D8483B' : '#1C76A2';
                return (
                  <g key={`thresh-${v}`}>
                    <line
                      x1={PAD.left}
                      y1={toY(v)}
                      x2={PAD.left + PLOT_W}
                      y2={toY(v)}
                      stroke={lineColor}
                      strokeWidth="1"
                      strokeDasharray="4 3"
                    />
                    <text
                      x={PAD.left - 6}
                      y={toY(v)}
                      textAnchor="end"
                      dominantBaseline="middle"
                      className="fill-gray-500 text-[10px]"
                    >
                      {v}
                    </text>
                  </g>
                );
              })}

              {/* Zone labels name the CONDITION, never an action.
                  ⚠️ They read the OPPOSITE way round to the band colour, and
                  that is correct: Squeezo measures RETAIL sentiment but is read
                  as a CONTRARIAN signal, so the two are always inverted. Above
                  65 retail is stretched bullish — hence the label — which a
                  contrarian fades, hence the red tint. Naming the retail state
                  rather than the market implication is what stops it being read
                  backwards; the implication is spelled out behind the ?.
                  These previously read "FADE" and "BOUNCE", which stated a
                  trade. Squeezo is one input among several, so a label telling
                  the reader what to do overstates what it can support. */}
              {yHigh > 65 && (
                <text
                  x={PAD.left + 6}
                  y={toY(yHigh) + 13}
                  className="fill-bear-red text-[10px] font-semibold"
                  style={{ letterSpacing: '0.5px' }}
                >
                  RETAIL EXTREME BULLISH
                </text>
              )}
              {yLow < 45 && (
                <text
                  x={PAD.left + 6}
                  y={toY(yLow) - 7}
                  className="fill-bull-blue text-[10px] font-semibold"
                  style={{ letterSpacing: '0.5px' }}
                >
                  RETAIL EXTREME BEARISH
                </text>
              )}

              {/* Data line — solid yellow */}
              {points.map((point, i) => {
                if (i === 0) return null;
                const prev = points[i - 1];
                return (
                  <line
                    key={i}
                    x1={toX(i - 1)}
                    y1={toY(prev.value)}
                    x2={toX(i)}
                    y2={toY(point.value)}
                    stroke="#C2A14E"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                );
              })}

              {/* Hover crosshair + dot */}
              {hoveredIdx !== null && points[hoveredIdx] && (() => {
                const hp = points[hoveredIdx];
                const hx = toX(hoveredIdx);
                const hy = toY(hp.value);
                return (
                  <g>
                    {/* Vertical crosshair */}
                    <line
                      x1={hx}
                      y1={PAD.top}
                      x2={hx}
                      y2={PAD.top + PLOT_H}
                      stroke="#6b7280"
                      strokeWidth="0.5"
                      strokeDasharray="2 2"
                    />
                    {/* Dot on data point */}
                    <circle cx={hx} cy={hy} r="3" fill="#C2A14E" stroke="#0A0A0A" strokeWidth="1" />
                  </g>
                );
              })()}

              {/* Hover tooltip */}
              {hoveredIdx !== null && points[hoveredIdx] && (() => {
                const hp = points[hoveredIdx];
                const hx = toX(hoveredIdx);
                const date = new Date(hp.created_at);
                const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                // Flip tooltip to left side if near right edge
                const flipX = hx > PAD.left + PLOT_W * 0.75;
                const tx = flipX ? hx - 8 : hx + 8;
                const anchor = flipX ? 'end' : 'start';
                return (
                  <g>
                    <text
                      x={tx}
                      y={PAD.top + 10}
                      textAnchor={anchor}
                      className="fill-accent-yellow text-[11px] font-semibold"
                    >
                      {hp.value}
                    </text>
                    <text
                      x={tx}
                      y={PAD.top + 22}
                      textAnchor={anchor}
                      className="fill-gray-400 text-[9px]"
                    >
                      {dateStr} {timeStr}
                    </text>
                  </g>
                );
              })()}

              {/* X-axis time labels (show ~5 evenly spaced) */}
              {points.length > 1 && [0, 0.25, 0.5, 0.75, 1].map((frac) => {
                const idx = Math.round(frac * (points.length - 1));
                const point = points[idx];
                const x = PAD.left + frac * PLOT_W;
                const date = new Date(point.created_at);
                const label = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                return (
                  <text
                    key={frac}
                    x={x}
                    y={CHART_H - 5}
                    textAnchor="middle"
                    className="fill-gray-500 text-[9px]"
                  >
                    {label}
                  </text>
                );
              })}
            </svg>
          )}
        </div>

        {/* Current reading and when it was set.
            Below the chart and OUTSIDE the admin block: the reading is for
            everyone, the Set-value controls underneath are not. */}
        <div className="flex items-center justify-center gap-6 px-4 pb-4 pt-1">
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">
              Current reading
            </div>
            {/* Deliberately NOT colour-coded. The chart above already bands the
                zones, and a second colour rule here is one more thing that can
                drift out of agreement with them. */}
            <div className="font-numeric text-3xl font-bold leading-none text-white">
              {Math.round(currentValue)}
              <span className="text-sm text-gray-400 font-medium"> / 100</span>
            </div>
          </div>

          {lastReadingAt && (
            <div className="border-l border-dark-border pl-6 text-center">
              <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">
                Last reading
              </div>
              <div className="font-numeric text-sm text-white leading-none">
                {fmtUsDate(lastReadingAt)}
                <span className="text-gray-300">
                  {' '}
                  {new Date(lastReadingAt).toLocaleTimeString('en-US', {
                    timeZone: TZ,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}
                  {/* Always ET. The value is set against the exchange's clock,
                      so rendering it in the viewer's would misdate it. */}
                  <span className="text-[9px] text-gray-400"> ET</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Edit controls — only for jdash_access users */}
        {canEdit && (
          <div className="px-4 pb-4 border-t border-dark-border pt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Set value:</span>
              <div className="flex-1" />

              {/* Input with arrows */}
              <div className="flex items-center">
                <button
                  onClick={() => incrementBy(-10)}
                  className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light rounded-l border border-dark-border border-r-0 transition-colors"
                  title="-10"
                >
                  --
                </button>
                <button
                  onClick={() => incrementBy(-1)}
                  className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light border-y border-dark-border transition-colors"
                  title="-1"
                >
                  -
                </button>

                <input
                  type="number"
                  min="0"
                  max="100"
                  value={inputValue}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v)) setInputValue(Math.min(100, Math.max(0, v)));
                  }}
                  /* Colour comes from the shared zone helper, so it cannot
                     disagree with the chart's bands or the header gauge. The
                     local `getValueColor` it replaced was inverted — it painted
                     65+ BLUE while `getSqueezoZone` calls 65+ bearish, the same
                     fault already fixed in the gauge ramp. */
                  style={{ color: getSqueezoZoneColor(inputValue) }}
                  className="w-12 px-1 py-1 text-center text-sm font-medium border-y border-dark-border bg-dark-bg focus:outline-none"
                />

                <button
                  onClick={() => incrementBy(1)}
                  className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light border-y border-dark-border transition-colors"
                  title="+1"
                >
                  +
                </button>
                <button
                  onClick={() => incrementBy(10)}
                  className="px-1.5 py-1 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light rounded-r border border-dark-border border-l-0 transition-colors"
                  title="+10"
                >
                  ++
                </button>
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!hasChanged || createMutation.isPending}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  hasChanged && !createMutation.isPending
                    ? 'bg-bull-blue hover:bg-bull-blue/80 text-white'
                    : 'bg-dark-border text-gray-500 cursor-not-allowed'
                } ${status === 'success' ? 'bg-bull-blue/50' : ''} ${status === 'error' ? 'bg-bear-red/50' : ''}`}
              >
                {createMutation.isPending ? '...' : status === 'success' ? '✓' : 'Set'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
